import React, { useState } from 'react';
import { toast } from 'sonner';
import { 
    RefreshCw, 
    ArrowLeftRight, 
    CheckCircle2, 
    AlertTriangle,
    Circle,
    ArrowRight,
    ArrowLeft,
    Database,
    Cloud,
    Loader2,
    Key,
    LogIn,
    ChevronDown,
    ChevronRight,
    Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { EmergentCostsSection } from '@/components/EmergentCostsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configuration des environnements
const PREVIEW_URL = "https://recipe-manager-43.preview.emergentagent.com";
const PRODUCTION_URL = "https://recipe-manager-43.emergent.host";

const COLLECTIONS = [
    { id: 'suppliers', name: 'Fournisseurs', endpoint: 'suppliers', nameField: 'name' },
    { id: 'sites', name: 'Sites', endpoint: 'sites', nameField: 'name' },
    { id: 'industrial_products', name: 'Produits Industriels', endpoint: 'industrial-products', nameField: 'name' },
    { id: 'supplier_products', name: 'Produits Fournisseurs', endpoint: 'supplier-products', nameField: 'supplier_name_product' },
    { id: 'orders', name: 'Commandes', endpoint: 'orders', nameField: 'order_number' },
    { id: 'inventories', name: 'Inventaires', endpoint: 'inventories', nameField: 'id' },
    { id: 'users', name: 'Utilisateurs', endpoint: 'users', nameField: 'email' },
    { id: 'order_form_templates', name: 'Formulaires de commande', endpoint: 'order-forms/templates', nameField: 'name' },
    { id: 'recipes', name: 'Recettes', syncExport: true, nameField: 'name', keyFields: ['name'], ignoreFields: ['id', 'created_at', 'updated_at'] },
    { id: 'pricing_clients', name: 'Clients tarifaires', syncExport: true, nameField: 'name', keyFields: ['name'], ignoreFields: ['id', 'created_at'] },
    { id: 'recipe_prices', name: 'Tarifs clients', syncExport: true, nameField: 'label', keyFields: ['client_name', 'recipe_name'], ignoreFields: ['client_id', 'recipe_id', 'label'] },
    { id: 'production_products', name: 'Produits de production', syncExport: true, nameField: 'name', keyFields: ['name'], ignoreFields: ['id', 'recipe_id', 'created_at'] },
    { id: 'production_clients', name: 'Clients de production', syncExport: true, nameField: 'label', keyFields: ['code'], ignoreFields: ['id', 'pricing_client_id', 'product_ids'] },
    { id: 'role_permissions', name: 'Profils & droits', syncExport: true, nameField: 'role', keyFields: ['role'] },
    { id: 'supplier_invoice_profiles', name: 'Profils factures IA', syncExport: true, nameField: 'label', keyFields: ['supplier_id'], ignoreFields: ['id', 'label', 'created_at', 'updated_at', 'calibrated_at'] },
    { id: 'inventory_form_templates', name: 'Modèles fiches inventaire', syncExport: true, nameField: 'name', keyFields: ['name'], ignoreFields: ['id', 'created_at', 'updated_at'] },
    { id: 'label_templates', name: 'Modèles étiquettes', syncExport: true, nameField: 'label', keyFields: ['name'], ignoreFields: ['id', 'client_id', 'label', 'imported_at'] },
    { id: 'label_data', name: 'Données étiquettes', syncExport: true, nameField: 'label', keyFields: ['client_code', 'product_name'], ignoreFields: ['product_id', 'client_id', 'template_id', 'label'] },
    { id: 'label_aliases', name: 'Associations étiquettes', syncExport: true, nameField: 'label', keyFields: ['client_code', 'key'], ignoreFields: ['client_id', 'product_id', 'label'] },
];

const keyOf = (col, item) => col.keyFields ? col.keyFields.map(k => item[k]).join('|') : item.id;

export default function SyncPage() {
    const [comparing, setComparing] = useState(false);
    const [comparison, setComparison] = useState(null);
    const [selectedCollections, setSelectedCollections] = useState([]);
    const [syncDirection, setSyncDirection] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [showCosts, setShowCosts] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [excluded, setExcluded] = useState({});
    const [openField, setOpenField] = useState(null);
    
    // Login dialog state
    const [loginDialog, setLoginDialog] = useState(false);
    const [loginTarget, setLoginTarget] = useState(null); // 'preview' or 'production'
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    
    // Tokens for both environments
    const [previewToken, setPreviewToken] = useState(null);
    const [productionToken, setProductionToken] = useState(null);

    const getToken = () => localStorage.getItem('token');

    const openLoginDialog = (target) => {
        setLoginTarget(target);
        setLoginEmail('');
        setLoginPassword('');
        setLoginDialog(true);
    };

    const handleLogin = async () => {
        if (!loginEmail || !loginPassword) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        
        setLoginLoading(true);
        const baseUrl = loginTarget === 'preview' ? PREVIEW_URL : PRODUCTION_URL;
        
        try {
            const response = await fetch(`${baseUrl}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: loginEmail, password: loginPassword })
            });
            
            if (response.ok) {
                const data = await response.json();
                const token = data.token || data.access_token;
                
                if (loginTarget === 'preview') {
                    setPreviewToken(token);
                } else {
                    setProductionToken(token);
                }
                
                toast.success(`Connecté à ${loginTarget === 'preview' ? 'Preview' : 'Production'}`);
                setLoginDialog(false);
            } else {
                const error = await response.json().catch(() => ({}));
                toast.error(typeof error.detail === 'string' ? error.detail : 'Échec de la connexion');
            }
        } catch (e) {
            toast.error(`Erreur de connexion à ${loginTarget === 'preview' ? 'Preview' : 'Production'} — réseau indisponible ou domaine bloqué (CORS). Si vous êtes sur sokuzai.net, vérifiez que la dernière version est bien déployée.`, { duration: 8000 });
            console.error('Login error:', e);
        } finally {
            setLoginLoading(false);
        }
    };

    const fetchData = async (baseUrl, col, token) => {
        const endpoint = typeof col === 'string' ? col : col.endpoint;
        try {
            // Collections exposées uniquement via l'export sync (recettes, tarifs, production, profils…)
            if (typeof col === 'object' && col.syncExport) {
                const resp = await fetch(`${baseUrl}/api/sync/export/${col.id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (resp.ok) {
                    const result = await resp.json();
                    return result.data || [];
                }
                return [];
            }
            // Pour les utilisateurs, essayer d'abord l'endpoint sync (inclut password_hash)
            // Si ça échoue, utiliser l'endpoint standard
            if (endpoint === 'users') {
                const syncResponse = await fetch(`${baseUrl}/api/sync/export/users`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (syncResponse.ok) {
                    const result = await syncResponse.json();
                    return result.data || [];
                }
                // Fallback sur l'endpoint standard
                const standardResponse = await fetch(`${baseUrl}/api/users`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (standardResponse.ok) {
                    return await standardResponse.json();
                }
                return [];
            }
            
            const response = await fetch(`${baseUrl}/api/${endpoint}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                return await response.json();
            }
        } catch (e) {
            console.error(`Error fetching ${endpoint}:`, e);
        }
        return [];
    };

    const compareEnvironments = async () => {
        if (!previewToken || !productionToken) {
            toast.error('Connectez-vous aux deux environnements d\'abord');
            return;
        }
        
        setComparing(true);
        setComparison(null);
        
        try {
            const results = {};
            
            for (const col of COLLECTIONS) {
                const [previewData, prodData] = await Promise.all([
                    fetchData(PREVIEW_URL, col, previewToken),
                    fetchData(PRODUCTION_URL, col, productionToken)
                ]);
                
                const previewIds = new Set(previewData.map(item => keyOf(col, item)));
                const prodIds = new Set(prodData.map(item => keyOf(col, item)));
                
                const onlyPreview = previewData.filter(item => !prodIds.has(keyOf(col, item)));
                const onlyProd = prodData.filter(item => !previewIds.has(keyOf(col, item)));
                const common = previewData.filter(item => prodIds.has(keyOf(col, item)));

                // Détecter les éléments présents des deux côtés mais différents (champs modifiés)
                const prodById = Object.fromEntries(prodData.map(item => [keyOf(col, item), item]));
                const modified = [];
                const ignore = new Set([...(col.ignoreFields || []), ...(col.keyFields || []), 'updated_at', 'created_at']);
                for (const pItem of common) {
                    const prodItem = prodById[keyOf(col, pItem)];
                    const keys = new Set([...Object.keys(pItem), ...Object.keys(prodItem)]);
                    const fields = [...keys].filter(k => !ignore.has(k) && JSON.stringify(pItem[k]) !== JSON.stringify(prodItem[k]));
                    if (!fields.length) continue;
                    modified.push({
                        id: keyOf(col, pItem),
                        name: pItem[col.nameField] || keyOf(col, pItem),
                        fields,
                        values: Object.fromEntries(fields.map(f => [f, { preview: pItem[f], prod: prodItem[f] }])),
                        updatedPreview: pItem.updated_at || null,
                        updatedProd: prodItem.updated_at || null,
                    });
                }
                
                results[col.id] = {
                    previewCount: previewData.length,
                    prodCount: prodData.length,
                    commonCount: common.length,
                    onlyPreview: onlyPreview.map(item => ({
                        id: keyOf(col, item),
                        name: item[col.nameField] || keyOf(col, item)
                    })),
                    onlyProd: onlyProd.map(item => ({
                        id: keyOf(col, item),
                        name: item[col.nameField] || keyOf(col, item)
                    })),
                    modified,
                    previewData,
                    prodData
                };
            }
            
            setComparison(results);
            setExcluded({});
            toast.success('Comparaison terminée');
        } catch (error) {
            toast.error('Erreur lors de la comparaison');
            console.error(error);
        } finally {
            setComparing(false);
        }
    };

    const getStatusIcon = (colData) => {
        if (!colData) return <Circle className="w-5 h-5 text-slate-300" />;
        
        if (colData.previewCount === 0 && colData.prodCount === 0) {
            return <Circle className="w-5 h-5 text-slate-300" />;
        }
        
        if (colData.onlyPreview.length === 0 && colData.onlyProd.length === 0 && (colData.modified || []).length === 0) {
            return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
        }
        
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    };

    const isIncluded = (colId, id) => !excluded[`${colId}|${id}`];

    const fmtVal = (v) => {
        if (v === undefined || v === null || v === '') return '—';
        const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
        return s.length > 160 ? s.slice(0, 160) + '…' : s;
    };

    const fmtDate = (iso) => {
        if (!iso) return null;
        const d = new Date(iso);
        if (isNaN(d)) return null;
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    const toggleItem = (colId, id) => {
        setExcluded(prev => {
            const k = `${colId}|${id}`;
            const next = { ...prev };
            if (next[k]) delete next[k]; else next[k] = true;
            return next;
        });
    };

    const toggleList = (colId, items) => {
        const allOn = items.every(it => isIncluded(colId, it.id));
        setExcluded(prev => {
            const next = { ...prev };
            items.forEach(it => {
                if (allOn) next[`${colId}|${it.id}`] = true;
                else delete next[`${colId}|${it.id}`];
            });
            return next;
        });
    };

    const selectedItemsCount = (colId, direction) => {
        const colData = comparison?.[colId];
        if (!colData) return 0;
        const news = direction === 'toProduction' ? colData.onlyPreview : colData.onlyProd;
        return news.filter(it => isIncluded(colId, it.id)).length
            + (colData.modified || []).filter(it => isIncluded(colId, it.id)).length;
    };

    const toggleCollection = (colId) => {
        setSelectedCollections(prev => 
            prev.includes(colId) 
                ? prev.filter(id => id !== colId)
                : [...prev, colId]
        );
    };

    const selectAllWithDiff = () => {
        if (!comparison) return;
        const withDiff = COLLECTIONS.filter(col => {
            const data = comparison[col.id];
            return data && (data.onlyPreview.length > 0 || data.onlyProd.length > 0 || (data.modified || []).length > 0);
        }).map(col => col.id);
        setSelectedCollections(withDiff);
    };

    const handleSync = async () => {
        if (!syncDirection || selectedCollections.length === 0) {
            toast.error('Sélectionnez une direction et au moins une collection');
            return;
        }
        setConfirmDialog(true);
    };

    const executeSync = async () => {
        setConfirmDialog(false);
        setSyncing(true);
        
        const sourceToken = syncDirection === 'toProduction' ? previewToken : productionToken;
        const targetUrl = syncDirection === 'toProduction' ? PRODUCTION_URL : PREVIEW_URL;
        const targetToken = syncDirection === 'toProduction' ? productionToken : previewToken;
        
        let successCount = 0;
        let errorCount = 0;
        
        try {
            // Prepare items to import
            const importItems = [];
            
            const orderedCollections = [...selectedCollections].sort((a, b) =>
                COLLECTIONS.findIndex(c => c.id === a) - COLLECTIONS.findIndex(c => c.id === b));
            for (const colId of orderedCollections) {
                const col = COLLECTIONS.find(c => c.id === colId);
                const colData = comparison[colId];
                
                const itemsToSync = (syncDirection === 'toProduction' 
                    ? colData.onlyPreview 
                    : colData.onlyProd).filter(it => isIncluded(colId, it.id));
                
                const sourceData = syncDirection === 'toProduction' 
                    ? colData.previewData 
                    : colData.prodData;
                
                for (const item of itemsToSync) {
                    const fullItem = sourceData.find(d => keyOf(col, d) === item.id);
                    if (fullItem) {
                        importItems.push({
                            collection: col.id,  // Utiliser l'ID de collection (avec underscore)
                            data: fullItem
                        });
                    }
                }

                // Éléments modifiés : pousser la version source pour écraser la cible
                for (const item of (colData.modified || []).filter(it => isIncluded(colId, it.id))) {
                    const fullItem = sourceData.find(d => keyOf(col, d) === item.id);
                    if (fullItem) {
                        importItems.push({ collection: col.id, data: fullItem });
                    }
                }
            }
            
            // Essayer d'abord l'endpoint sync/import
            let response = await fetch(`${targetUrl}/api/sync/import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${targetToken}`
                },
                body: JSON.stringify({ items: importItems, overwrite: true })
            });
            
            // Si sync/import n'existe pas, utiliser les endpoints standards
            if (!response.ok && response.status === 404) {
                console.log('sync/import not available, using standard endpoints');
                for (const item of importItems) {
                    try {
                        // Mapper la collection vers l'endpoint
                        const endpointMap = {
                            'suppliers': 'suppliers',
                            'sites': 'sites',
                            'industrial_products': 'industrial-products',
                            'supplier_products': 'supplier-products',
                            'orders': 'orders',
                            'inventories': 'inventories',
                            'users': 'users',
                            'order_form_templates': 'order-forms/templates'
                        };
                        const endpoint = endpointMap[item.collection] || item.collection;
                        
                        // Pour les utilisateurs, on ne peut pas synchroniser le password_hash
                        // via l'endpoint standard. On doit générer un mot de passe temporaire.
                        let dataToSend = item.data;
                        if (item.collection === 'users') {
                            // L'endpoint standard attend 'password' pas 'password_hash'
                            // Générer un mot de passe temporaire
                            const tempPassword = 'TempSync' + Math.random().toString(36).slice(-8) + '!';
                            dataToSend = {
                                email: item.data.email,
                                name: item.data.name,
                                password: tempPassword,
                                role: item.data.role || 'operator',
                                allowed_sites: item.data.allowed_sites
                            };
                            console.log(`⚠️ Utilisateur ${item.data.email} créé avec mot de passe temporaire`);
                            toast.warning(`Utilisateur ${item.data.email} créé - mot de passe à réinitialiser`);
                        }
                        
                        const createResponse = await fetch(`${targetUrl}/api/${endpoint}`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${targetToken}`
                            },
                            body: JSON.stringify(dataToSend)
                        });
                        
                        if (createResponse.ok) {
                            successCount++;
                        } else {
                            errorCount++;
                            const err = await createResponse.json();
                            console.log(`Error creating ${item.collection}:`, err);
                        }
                    } catch (e) {
                        errorCount++;
                        console.error(`Error syncing ${item.collection}:`, e);
                    }
                }
                
                if (successCount > 0) {
                    toast.success(`${successCount} élément(s) synchronisé(s)`);
                }
                if (errorCount > 0) {
                    toast.error(`${errorCount} erreur(s) lors de la synchronisation`);
                }
            } else if (response.ok) {
                const result = await response.json();
                successCount = result.success;
                errorCount = result.errors;
                const updatedCount = result.updated || 0;
                const skippedCount = result.skipped || 0;
                // Ancien backend (sans overwrite) : les modifiés reviennent en "exists"
                const legacySkipped = (result.details || []).filter(d => d.status === 'exists').length;
                
                if (successCount > 0 || updatedCount > 0) {
                    toast.success(`${successCount} ajouté(s), ${updatedCount} mis à jour`);
                }
                if (skippedCount === 0 && legacySkipped > 0) {
                    toast.warning(`${legacySkipped} élément(s) modifié(s) non mis à jour : l'environnement cible utilise une ancienne version — redéployez pour activer la mise à jour des modifiés`);
                }
                if (successCount === 0 && updatedCount === 0 && legacySkipped === 0 && errorCount === 0) {
                    toast.info('Rien à synchroniser');
                }
                if (errorCount > 0) {
                    toast.error(`${errorCount} erreur(s) lors de la synchronisation`);
                    console.log('Sync errors:', result.details);
                }
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors de la synchronisation');
            }
            
            // Refresh comparison
            await compareEnvironments();
            setSelectedCollections([]);
            setSyncDirection(null);
            
        } catch (error) {
            toast.error('Erreur lors de la synchronisation');
            console.error(error);
        } finally {
            setSyncing(false);
        }
    };

    const isConnected = (env) => {
        return env === 'preview' ? !!previewToken : !!productionToken;
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="sync-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Synchronisation</h1>
                    <p className="text-slate-500 mt-1">Comparez et synchronisez les données entre Preview et Production</p>
                </div>
                <Button 
                    onClick={compareEnvironments} 
                    disabled={comparing || !previewToken || !productionToken}
                    className="btn-primary"
                    data-testid="compare-btn"
                >
                    {comparing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    {comparing ? 'Comparaison...' : 'Comparer les environnements'}
                </Button>
            </div>

            {/* Coûts de développement Emergent (masqué par défaut) */}
            <div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCosts(v => !v)}
                    className="text-slate-500 hover:text-slate-800 px-2"
                    data-testid="toggle-costs-btn"
                >
                    {showCosts ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                    <Wallet className="w-4 h-4 mr-1.5" />
                    Coûts de développement Sokuzai
                </Button>
                {showCosts && <div className="mt-2"><EmergentCostsSection /></div>}
            </div>

            {/* Environment Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className={`border-2 ${isConnected('preview') ? 'border-blue-400 bg-blue-50/50' : 'border-blue-200 bg-slate-50'}`}>
                    <CardHeader className="pb-2">
                        <CardTitle className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-blue-700">
                                <Database className="w-5 h-5" />
                                Preview
                            </span>
                            {isConnected('preview') ? (
                                <Badge className="bg-blue-500">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Connecté
                                </Badge>
                            ) : (
                                <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => openLoginDialog('preview')}
                                    className="text-blue-600 border-blue-300 hover:bg-blue-100"
                                >
                                    <LogIn className="w-4 h-4 mr-1" />
                                    Connexion
                                </Button>
                            )}
                        </CardTitle>
                        <CardDescription className="text-blue-600 text-xs break-all">
                            {PREVIEW_URL}
                        </CardDescription>
                    </CardHeader>
                </Card>
                <Card className={`border-2 ${isConnected('production') ? 'border-emerald-400 bg-emerald-50/50' : 'border-emerald-200 bg-slate-50'}`}>
                    <CardHeader className="pb-2">
                        <CardTitle className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-emerald-700">
                                <Cloud className="w-5 h-5" />
                                Production
                            </span>
                            {isConnected('production') ? (
                                <Badge className="bg-emerald-500">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Connecté
                                </Badge>
                            ) : (
                                <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => openLoginDialog('production')}
                                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-100"
                                >
                                    <LogIn className="w-4 h-4 mr-1" />
                                    Connexion
                                </Button>
                            )}
                        </CardTitle>
                        <CardDescription className="text-emerald-600 text-xs break-all">
                            {PRODUCTION_URL}
                        </CardDescription>
                    </CardHeader>
                </Card>
            </div>

            {/* Connection Required Message */}
            {(!previewToken || !productionToken) && (
                <Card className="border-amber-200 bg-amber-50/50">
                    <CardContent className="py-6">
                        <div className="flex items-center gap-3">
                            <Key className="w-8 h-8 text-amber-500" />
                            <div>
                                <p className="font-medium text-amber-800">Connexion requise</p>
                                <p className="text-sm text-amber-600">
                                    Connectez-vous aux deux environnements pour pouvoir comparer et synchroniser les données.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Comparison Results */}
            {comparison && (
                <>
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <ArrowLeftRight className="w-5 h-5" />
                                Résultat de la comparaison
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b text-sm text-slate-500">
                                            <th className="text-left py-3 px-2 w-8"></th>
                                            <th className="text-left py-3 px-2">Collection</th>
                                            <th className="text-center py-3 px-2">
                                                <span className="text-blue-600">Preview</span>
                                            </th>
                                            <th className="text-center py-3 px-2">
                                                <span className="text-emerald-600">Production</span>
                                            </th>
                                            <th className="text-center py-3 px-2">En commun</th>
                                            <th className="text-left py-3 px-2">Différences</th>
                                            <th className="text-center py-3 px-2 w-10">
                                                <Checkbox 
                                                    checked={selectedCollections.length === COLLECTIONS.filter(c => {
                                                        const d = comparison[c.id];
                                                        return d && (d.onlyPreview.length > 0 || d.onlyProd.length > 0 || (d.modified || []).length > 0);
                                                    }).length && selectedCollections.length > 0}
                                                    onCheckedChange={selectAllWithDiff}
                                                />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {COLLECTIONS.map(col => {
                                            const data = comparison[col.id];
                                            const modCount = (data?.modified || []).length;
                                            const hasDiff = data && (data.onlyPreview.length > 0 || data.onlyProd.length > 0 || modCount > 0);
                                            const isExpanded = !!expandedRows[col.id];
                                            
                                            return (
                                                <React.Fragment key={col.id}>
                                                <tr
                                                    className={`border-b last:border-0 hover:bg-slate-50 ${hasDiff ? 'cursor-pointer' : ''}`}
                                                    onClick={() => hasDiff && setExpandedRows(prev => ({ ...prev, [col.id]: !prev[col.id] }))}
                                                    data-testid={`sync-row-${col.id}`}
                                                >
                                                    <td className="py-3 px-2">
                                                        <div className="flex items-center gap-1">
                                                            {getStatusIcon(data)}
                                                            {hasDiff && (isExpanded
                                                                ? <ChevronDown className="w-4 h-4 text-slate-400" />
                                                                : <ChevronRight className="w-4 h-4 text-slate-400" />)}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-2 font-medium">{col.name}</td>
                                                    <td className="py-3 px-2 text-center">
                                                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                                            {data?.previewCount || 0}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-2 text-center">
                                                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                                            {data?.prodCount || 0}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-2 text-center text-slate-500">
                                                        {data?.commonCount || 0}
                                                    </td>
                                                    <td className="py-3 px-2">
                                                        <div className="flex flex-wrap gap-1">
                                                            {data?.onlyPreview.length > 0 && (
                                                                <Badge className="bg-blue-100 text-blue-700 text-xs">
                                                                    +{data.onlyPreview.length} Preview
                                                                </Badge>
                                                            )}
                                                            {data?.onlyProd.length > 0 && (
                                                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                                                    +{data.onlyProd.length} Prod
                                                                </Badge>
                                                            )}
                                                            {modCount > 0 && (
                                                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                                                    ≠ {modCount} modifié{modCount > 1 ? 's' : ''}
                                                                </Badge>
                                                            )}
                                                            {!hasDiff && (
                                                                <span className="text-sm text-slate-400">Identique</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                                                        {hasDiff && (
                                                            <Checkbox 
                                                                checked={selectedCollections.includes(col.id)}
                                                                onCheckedChange={() => toggleCollection(col.id)}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="border-b last:border-0 bg-slate-50/70">
                                                        <td colSpan={7} className="py-3 px-4">
                                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                                                <div>
                                                                    <p className="font-semibold text-blue-700 mb-1.5 flex items-center gap-2">
                                                                        {data.onlyPreview.length > 0 && (
                                                                            <Checkbox
                                                                                checked={data.onlyPreview.every(it => isIncluded(col.id, it.id))}
                                                                                onCheckedChange={() => toggleList(col.id, data.onlyPreview)}
                                                                                data-testid={`sync-toggle-all-preview-${col.id}`}
                                                                            />
                                                                        )}
                                                                        Uniquement en Preview ({data.onlyPreview.filter(it => isIncluded(col.id, it.id)).length}/{data.onlyPreview.length})
                                                                    </p>
                                                                    <div className="max-h-48 overflow-y-auto space-y-0.5 pr-2">
                                                                        {data.onlyPreview.length === 0 && <span className="text-slate-400">—</span>}
                                                                        {data.onlyPreview.map(item => (
                                                                            <label key={item.id} className={`flex items-center gap-2 cursor-pointer ${isIncluded(col.id, item.id) ? 'text-slate-700' : 'text-slate-400 line-through'}`} title={item.id}>
                                                                                <Checkbox
                                                                                    checked={isIncluded(col.id, item.id)}
                                                                                    onCheckedChange={() => toggleItem(col.id, item.id)}
                                                                                    data-testid={`sync-item-${col.id}-${item.id}`}
                                                                                />
                                                                                <span className="truncate">{item.name}</span>
                                                                            </label>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <p className="font-semibold text-emerald-700 mb-1.5 flex items-center gap-2">
                                                                        {data.onlyProd.length > 0 && (
                                                                            <Checkbox
                                                                                checked={data.onlyProd.every(it => isIncluded(col.id, it.id))}
                                                                                onCheckedChange={() => toggleList(col.id, data.onlyProd)}
                                                                                data-testid={`sync-toggle-all-prod-${col.id}`}
                                                                            />
                                                                        )}
                                                                        Uniquement en Production ({data.onlyProd.filter(it => isIncluded(col.id, it.id)).length}/{data.onlyProd.length})
                                                                    </p>
                                                                    <div className="max-h-48 overflow-y-auto space-y-0.5 pr-2">
                                                                        {data.onlyProd.length === 0 && <span className="text-slate-400">—</span>}
                                                                        {data.onlyProd.map(item => (
                                                                            <label key={item.id} className={`flex items-center gap-2 cursor-pointer ${isIncluded(col.id, item.id) ? 'text-slate-700' : 'text-slate-400 line-through'}`} title={item.id}>
                                                                                <Checkbox
                                                                                    checked={isIncluded(col.id, item.id)}
                                                                                    onCheckedChange={() => toggleItem(col.id, item.id)}
                                                                                    data-testid={`sync-item-${col.id}-${item.id}`}
                                                                                />
                                                                                <span className="truncate">{item.name}</span>
                                                                            </label>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <p className="font-semibold text-amber-700 mb-1.5 flex items-center gap-2">
                                                                        {modCount > 0 && (
                                                                            <Checkbox
                                                                                checked={(data.modified || []).every(it => isIncluded(col.id, it.id))}
                                                                                onCheckedChange={() => toggleList(col.id, data.modified || [])}
                                                                                data-testid={`sync-toggle-all-modified-${col.id}`}
                                                                            />
                                                                        )}
                                                                        <span>
                                                                            Modifiés ({(data.modified || []).filter(it => isIncluded(col.id, it.id)).length}/{modCount})
                                                                            <span className="font-normal text-slate-400"> — la version source écrase la cible lors de la synchro</span>
                                                                        </span>
                                                                    </p>
                                                                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-2">
                                                                        {modCount === 0 && <span className="text-slate-400">—</span>}
                                                                        {(data.modified || []).map(item => (
                                                                            <div key={item.id} title={item.id}>
                                                                                <label className={`flex items-center gap-2 cursor-pointer ${isIncluded(col.id, item.id) ? 'text-slate-700' : 'text-slate-400 line-through'}`}>
                                                                                    <Checkbox
                                                                                        checked={isIncluded(col.id, item.id)}
                                                                                        onCheckedChange={() => toggleItem(col.id, item.id)}
                                                                                        data-testid={`sync-item-${col.id}-${item.id}`}
                                                                                    />
                                                                                    <span className="truncate">{item.name}</span>
                                                                                </label>
                                                                                {(item.updatedPreview || item.updatedProd) && (() => {
                                                                                    const pNewer = item.updatedPreview && (!item.updatedProd || item.updatedPreview > item.updatedProd);
                                                                                    const prNewer = item.updatedProd && (!item.updatedPreview || item.updatedProd > item.updatedPreview);
                                                                                    return (
                                                                                        <div className="ml-7 text-[10px] text-slate-500" data-testid={`sync-dates-${col.id}-${item.id}`}>
                                                                                            <span className={pNewer ? 'font-bold text-blue-700' : 'text-blue-600'}>
                                                                                                Preview : {fmtDate(item.updatedPreview) || '?'}{pNewer ? ' ← plus récent' : ''}
                                                                                            </span>
                                                                                            <span className="mx-1">·</span>
                                                                                            <span className={prNewer ? 'font-bold text-emerald-700' : 'text-emerald-600'}>
                                                                                                Prod : {fmtDate(item.updatedProd) || '?'}{prNewer ? ' ← plus récent' : ''}
                                                                                            </span>
                                                                                        </div>
                                                                                    );
                                                                                })()}
                                                                                <div className="flex flex-wrap gap-1 ml-7 mt-0.5">
                                                                                    {item.fields.slice(0, 8).map(f => {
                                                                                        const fk = `${col.id}|${item.id}|${f}`;
                                                                                        const isOpen = openField === fk;
                                                                                        return (
                                                                                            <button key={f} type="button"
                                                                                                onClick={() => setOpenField(isOpen ? null : fk)}
                                                                                                title="Cliquer pour voir les valeurs Preview et Production"
                                                                                                className={`px-1.5 py-0.5 rounded border text-[10px] cursor-pointer transition-colors ${isOpen ? 'bg-amber-200 border-amber-400 text-amber-900' : 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'}`}
                                                                                                data-testid={`sync-field-${col.id}-${item.id}-${f}`}>
                                                                                                {f}
                                                                                            </button>
                                                                                        );
                                                                                    })}
                                                                                    {item.fields.length > 8 && (
                                                                                        <span className="text-[10px] text-slate-400">+{item.fields.length - 8} champs</span>
                                                                                    )}
                                                                                </div>
                                                                                {item.fields.slice(0, 8).map(f => {
                                                                                    const fk = `${col.id}|${item.id}|${f}`;
                                                                                    if (openField !== fk) return null;
                                                                                    const v = item.values?.[f] || {};
                                                                                    return (
                                                                                        <div key={fk} className="ml-7 mt-1 mb-1 rounded border border-slate-200 bg-white p-2 space-y-1 text-[11px]" data-testid={`sync-field-detail-${col.id}-${item.id}-${f}`}>
                                                                                            <div className="flex gap-1.5">
                                                                                                <span className="shrink-0 font-semibold text-blue-700">Preview :</span>
                                                                                                <span className="text-slate-700 break-all">{fmtVal(v.preview)}</span>
                                                                                            </div>
                                                                                            <div className="flex gap-1.5">
                                                                                                <span className="shrink-0 font-semibold text-emerald-700">Production :</span>
                                                                                                <span className="text-slate-700 break-all">{fmtVal(v.prod)}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <p className="text-[11px] text-slate-400 mt-2">Décochez un élément pour l'exclure de la synchronisation. Seuls les éléments cochés côté source seront envoyés.</p>
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Sync Actions */}
                    {selectedCollections.length > 0 && (
                        <Card className="border-amber-200 bg-amber-50/30">
                            <CardHeader>
                                <CardTitle className="text-lg">Synchroniser les données sélectionnées</CardTitle>
                                <CardDescription>
                                    {selectedCollections.length} collection(s) sélectionnée(s)
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Button
                                        variant={syncDirection === 'toProduction' ? 'default' : 'outline'}
                                        className={syncDirection === 'toProduction' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                                        onClick={() => setSyncDirection('toProduction')}
                                    >
                                        <ArrowRight className="w-4 h-4 mr-2" />
                                        Preview → Production
                                    </Button>
                                    <Button
                                        variant={syncDirection === 'toPreview' ? 'default' : 'outline'}
                                        className={syncDirection === 'toPreview' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                                        onClick={() => setSyncDirection('toPreview')}
                                    >
                                        <ArrowLeft className="w-4 h-4 mr-2" />
                                        Production → Preview
                                    </Button>
                                    
                                    {syncDirection && (
                                        <Button 
                                            onClick={handleSync}
                                            disabled={syncing}
                                            className="ml-auto bg-amber-600 hover:bg-amber-700"
                                        >
                                            {syncing ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <RefreshCw className="w-4 h-4 mr-2" />
                                            )}
                                            {syncing ? 'Synchronisation...' : 'Lancer la synchronisation'}
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* Empty State */}
            {!comparison && !comparing && previewToken && productionToken && (
                <Card className="border-dashed">
                    <CardContent className="py-12 text-center">
                        <ArrowLeftRight className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500 mb-4">
                            Cliquez sur "Comparer les environnements" pour voir les différences
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Login Dialog */}
            <Dialog open={loginDialog} onOpenChange={setLoginDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {loginTarget === 'preview' ? (
                                <Database className="w-5 h-5 text-blue-600" />
                            ) : (
                                <Cloud className="w-5 h-5 text-emerald-600" />
                            )}
                            Connexion à {loginTarget === 'preview' ? 'Preview' : 'Production'}
                        </DialogTitle>
                        <DialogDescription>
                            Entrez vos identifiants pour {loginTarget === 'preview' ? PREVIEW_URL : PRODUCTION_URL}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Identifiant</Label>
                            <Input 
                                id="email" 
                                type="text" 
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                                placeholder="votre identifiant (ex: eeginer)"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Mot de passe</Label>
                            <Input 
                                id="password" 
                                type="password" 
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                placeholder="••••••••"
                                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLoginDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleLogin} 
                            disabled={loginLoading}
                            className={loginTarget === 'preview' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                        >
                            {loginLoading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <LogIn className="w-4 h-4 mr-2" />
                            )}
                            Se connecter
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm Dialog */}
            <AlertDialog open={confirmDialog} onOpenChange={setConfirmDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la synchronisation</AlertDialogTitle>
                        <AlertDialogDescription>
                            Vous allez synchroniser {selectedCollections.length} collection(s) de{' '}
                            <strong>{syncDirection === 'toProduction' ? 'Preview vers Production' : 'Production vers Preview'}</strong>
                            {' '}({selectedCollections.reduce((n, c) => n + selectedItemsCount(c, syncDirection), 0)} élément(s) coché(s)).
                            <br /><br />
                            Les éléments manquants seront créés et les éléments modifiés seront écrasés par la version source.
                            {syncDirection === 'toProduction' && selectedCollections.some(c => ['orders', 'inventories', 'users'].includes(c)) && (
                                <>
                                    <br /><br />
                                    <span className="text-red-600 font-semibold" data-testid="sync-danger-warning">
                                        ⚠️ DANGER : vous poussez des données transactionnelles (commandes / inventaires / utilisateurs) vers la PRODUCTION.
                                        Les versions preview écraseront le travail quotidien de production. Ne confirmez que si c'est vraiment voulu.
                                    </span>
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={executeSync} className="bg-amber-600 hover:bg-amber-700">
                            Confirmer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
